import React from "react";
import { isPayerPage } from "../utils/payerPages";

/*
    What anyone sees when the app's error boundary catches something.

    Honeybadger's boundary renders its own component otherwise, which prints "An Error
    Occurred", the raw error and a JSON dump of the React component stack. A payer opening
    an invoice from an email then reads a TypeError where their invoice was, and it names
    our internals to someone who is not our user.

    It is deliberately plain HTML with inline styles. This sits outside ThemeProvider in
    _app.js, so there is no Grommet theme and no styled-components theme up here, and the
    one thing it must never do is throw on its own while rendering the failure of
    everything below it.

    It wraps the whole app, so the next step depends on who is reading. On the pages a
    customer of a customer reaches from a link - /p/, /d/ and /hi/ - that is the business
    that sent it; they have no account with us and nothing to tell our support. Everywhere
    else it is a merchant in their own dashboard, and the business that sent them a link
    does not exist. The path is read from window.location rather than the router: this only
    renders in the browser after something crashed, and it must not depend on anything that
    might have gone down with it.

    It only helps when React can still render. Injected code that breaks appendChild for
    every node React creates takes this down with everything else and leaves a blank page;
    nothing rendered through React can survive that, this included. What it does survive is
    the variant seen in production, which only trips on links - so it contains none.

    Not every error here is ours. A browser extension or an in-app browser that patches DOM
    methods can throw inside React's commit and take the tree down with it; those arrive
    looking like "Converting circular structure to JSON" from injected code, and
    honeybadger.browser.config.js drops them from reporting. Reloading genuinely fixes some
    of them, which is why that is the offer rather than a dead end.
*/
const styles = {
    wrap: {
        minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px', boxSizing: 'border-box',
    },
    card: {
        maxWidth: '420px', textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif', color: '#333',
    },
    heading: { fontSize: '18px', fontWeight: 500, margin: '0 0 8px' },
    body: { fontSize: '14px', lineHeight: 1.5, margin: '0 0 20px', color: '#666' },
    email: { fontWeight: 500, userSelect: 'all', wordBreak: 'break-all' },
    button: {
        font: 'inherit', fontSize: '14px', padding: '8px 20px', borderRadius: '4px',
        border: '1px solid #ccc', background: '#fff', cursor: 'pointer', color: '#333',
    },
};

/*
    Who the payer should go back to, read from the page's own props: the same name and email
    the page itself was showing them, so the fallback names someone they recognise.

    Each payer page carries the merchant in a different shape - the invoice's preference and
    sender_details on /p/ (primary contact's email first, as CompanyContactFooter shows it),
    the deposit's merchant on /d/, companyDetails on /hi/. Everything is optional and checked
    as a string: this runs precisely when something has gone wrong, possibly with the props
    themselves, so it answers with whatever it can find rather than throwing.
*/
const text = (value) => (typeof value === 'string' && value.trim() ? value.trim() : undefined);
const looksLikeEmail = (value) => (value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? value : undefined);

export const merchantContact = (pageProps) => {
    const props = pageProps && typeof pageProps === 'object' ? pageProps : {};
    const { invoice, deposit, companyDetails } = props;

    if (invoice && typeof invoice === 'object') {
        return {
            name: text(invoice.preference?.business_name) || text(invoice.preference?.legal_name),
            email: looksLikeEmail(text(invoice.sender_details?.primary_contact?.email) || text(invoice.sender_details?.email)),
        };
    }
    if (deposit && typeof deposit === 'object') {
        return {
            name: text(deposit.merchant?.business_name) || text(deposit.merchant?.legal_name),
            email: looksLikeEmail(text(deposit.merchant?.email)),
        };
    }
    if (companyDetails && typeof companyDetails === 'object') {
        return {
            name: text(companyDetails.business_name),
            email: looksLikeEmail(text(companyDetails.email)),
        };
    }
    return {};
};

/*
    Plain text, deliberately not a mailto link. The fault this fallback was built for came from
    injected code that stringifies links as React appends them - it threw on an <a> in the
    invoice footer while every div above it went in untouched - so a link here would fail the
    same way while rendering the screen meant to recover from it. userSelect: all makes the
    address one tap to select and copy on a phone.
*/
const Email = ({ email }) => (<span style={styles.email}>{email}</span>);

// The sentence after "Reloading usually fixes it." for a payer, as specific as the props allow.
const PayerNextStep = ({ merchant }) => {
    const { name, email } = merchant || {};
    if (name && email) return (<>If it keeps happening, contact {name} at <Email email={email} />.</>);
    if (name) return (<>If it keeps happening, contact {name}.</>);
    if (email) return (<>If it keeps happening, contact the business that sent you this link at <Email email={email} />.</>);
    return (<>If it keeps happening, the business that sent you this link can help.</>);
};

const AppErrorFallback = ({ merchant }) => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    // Signup isn't a payer page: whoever is signing up is becoming our merchant, so our support
    // is the right place for them.
    return (
        <div style={styles.wrap}>
            <div style={styles.card}>
                <p style={styles.heading}>This page didn&apos;t load properly</p>
                <p style={styles.body}>
                    {isPayerPage(path)
                        ? (<>Reloading usually fixes it. <PayerNextStep merchant={merchant} /></>)
                        : 'Reloading usually fixes it. If it keeps happening, contact us at help@streamlinedpayments.com.'}
                </p>
                <button
                    type="button"
                    style={styles.button}
                    onClick={() => { if (typeof window !== 'undefined') window.location.reload(); }}>
                    Reload the page
                </button>
            </div>
        </div>
    );
};

export default AppErrorFallback;
